import axios from "axios";
import React, { createContext, useReducer, useEffect, useState } from "react";
import { useTenant } from "../hooks";
import { toast } from "react-toastify";

const AccountCenterContext = createContext();

/* ============================= */
/* 2️⃣  Recursive Helpers */
/* ============================= */

const addAccountRecursive = (nodes, parentId, newAccount) => {
  return nodes.map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        childs: [...(node.childs || []), newAccount],
      };
    }

    if (node.childs?.length) {
      return {
        ...node,
        childs: addAccountRecursive(node.childs, parentId, newAccount),
      };
    }

    return node;
  });
};

const editAccountRecursive = (nodes, payload) => {
  return nodes.map((node) => {
    if (node.id === payload.id) {
      return {
        ...node,
        account_no: payload.account_no,
        name_ar: payload.name_ar,
        name_en: payload.name_en,
        account_type: payload.account_type,
      };
    }

    if (node.childs?.length) {
      return {
        ...node,
        childs: editAccountRecursive(node.childs, payload),
      };
    }

    return node;
  });
};

const deleteAccountRecursive = (nodes, id) => {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({
      ...node,
      childs: node.childs ? deleteAccountRecursive(node.childs, id) : [],
    }));
};

/* ============================= */
/* 3️⃣  Reducer */
/* ============================= */

const AccountCenterReducer = (state, action) => {
  switch (action.type) {
    case "SET_ACCOUNTS":
      return action.payload;

    case "ADD_ACCOUNT":
      if (!action.payload.parentId) {
        return [...state, action.payload.newAccount];
      }

      return addAccountRecursive(
        state,
        action.payload.parentId,
        action.payload.newAccount,
      );

    case "EDIT_ACCOUNT":
      return editAccountRecursive(state, action.payload);

    case "DELETE_ACCOUNT":
      return deleteAccountRecursive(state, action.payload.id);

    default:
      return state;
  }
};

/* ============================= */
/* 4️⃣  Provider */
/* ============================= */

export const AccountCenterProvider = ({ children }) => {
  const BaseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const { tenantId, companySelected, branchSelected } = useTenant();
  const [accounts, dispatch] = useReducer(AccountCenterReducer, []);
  const fetchAccounts = async () => {
    if (!tenantId || !companySelected?.value || !branchSelected?.value) return;

    try {
      setLoading(true);

      const response = await axios.post(`${BaseUrl}/tree-cost-center`, {
        tenant_id: tenantId,
        company_id: companySelected?.value,
        branch_id: branchSelected?.value,
      });

      const accounts = response?.data?.data?.treeParent || [];

      dispatch({
        type: "SET_ACCOUNTS",
        payload: accounts,
      });
    } catch (error) {
      console.error(
        "Failed to fetch cost center accounts",
        error?.response?.data || error?.message || error,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId && companySelected?.value && branchSelected?.value) {
      fetchAccounts();
    }
  }, [tenantId, companySelected?.value, branchSelected?.value]);
  const addAccount = async (payload) => {
    if (!tenantId || !companySelected?.value || !branchSelected?.value) {
      throw new Error("Missing tenant/company/branch while adding cost center");
    }

    try {
      setLoadingSave(true);

      const response = await axios.post(
        `${BaseUrl}/tree-cost-center/store`,
        payload,
      );
      await fetchAccounts();
      
      return response;
    } catch (err) {
      console.error(
        "Failed to add cost center account",
        err?.response?.data || err?.message || err,
      );
      throw err;
    } finally {
      setLoadingSave(false);
    }
  };
  const editAccount = async (payload, id) => {
    if (!id) {
      throw new Error("Missing cost center account id while editing");
    }
    if (!tenantId || !companySelected?.value || !branchSelected?.value) {
      throw new Error("Missing tenant/company/branch while editing cost center");
    }

    try {
      setLoadingSave(true);

   

      const response = await axios.post(`${BaseUrl}/tree-cost-center/store`, {
        id,
        ...payload,
      });
      const updatedAccount = {
        id,
        ...response?.data?.data,
      };
      dispatch({
        type: "EDIT_ACCOUNT",
        payload: updatedAccount,
      });

      return response;
    } catch (err) {
      console.error(
        "Failed to edit cost center account",
        err?.response?.data || err?.message || err,
      );
      throw err;
    } finally {
      setLoadingSave(false);
    }
  };
  const deleteAccount = async (id) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${BaseUrl}/tree-cost-center/delete/${id}`,
      );
      dispatch({
        type: "DELETE_ACCOUNT",
        payload: { id },
      });
      return response;
    } catch (err) {
      console.error(
        "Failed to delete cost center account",
        err?.response?.data || err?.message || err,
      );
            toast.error(err?.response?.data?.message);
      
      return null;
    } finally {
      setLoading(false);
    }
  };
  return (
    <AccountCenterContext.Provider
      value={{
        accounts,
        dispatch,
        loading,
        loadingSave,
        addAccount,
        editAccount,
        deleteAccount,
      }}
    >
      {children}
    </AccountCenterContext.Provider>
  );
};

export default AccountCenterContext;
